import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import SectionEyebrow from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/PrimaryParamsSection/components/SectionEyebrow';

import useData from './hooks/useData';
import useTranslation from './hooks/useTranslation';

import GapPolicyPicker from './components/GapPolicyPicker';
import PrescriptionStatusRow from './components/PrescriptionStatusRow';
import RateColumnPicker from './components/RateColumnPicker';
import RateLegend from './components/RateLegend';
import ShapefileDropZone from './components/ShapefileDropZone';
import WarningDialog from './components/WarningDialog';
import { ERROR_BANNER_TEST_ID, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const PrescriptionSection: FunctionComponent<Props> = ({
    attachError,
    attachPrescription,
    className = '',
    detachPrescription,
    isAttachingPrescription,
    isDetachingPrescription,
    onReplan,
    rateBounds,
    testId = TEST_ID,
    zoneCount
}) => {
    const t = useTranslation();
    const data = useData({
        attachError,
        attachPrescription,
        detachPrescription,
        isAttachingPrescription,
        isDetachingPrescription,
        onReplan
    });
    const isWarningBanner = !attachError && data.errorMessage !== null;
    const bannerClass = `${styles.errorBanner} ${isWarningBanner ? styles.warning : ''}`;
    const hasPrescription = zoneCount !== null;

    return (
        <VerticalContainer className={`${className} ${styles.prescriptionSection}`} testId={testId}>
            <SectionEyebrow eyebrowText={t.TITLE} titleText={t.UPLOAD_HINT} />

            {data.errorMessage && (
                <Wrapper className={bannerClass} testId={ERROR_BANNER_TEST_ID}>
                    {data.errorMessage}
                </Wrapper>
            )}

            {hasPrescription ? (
                <PrescriptionStatusRow
                    className={styles.footer}
                    detachText={t.DETACH}
                    isDetachDisabled={isDetachingPrescription}
                    onDetach={data.handleDetach}
                    zoneCountText={t.ZONE_COUNT(zoneCount)}
                />
            ) : (
                <ShapefileDropZone
                    hint={t.UPLOAD_HINT}
                    isProcessing={data.isProcessing}
                    onFilesSelected={data.handleFilesSelected}
                    processingText={t.UPLOAD_PROCESSING}
                    triggerText={t.RATE_COLUMN_CONFIRM}
                />
            )}

            <GapPolicyPicker
                baseRateLabel={t.GAP_POLICY_BASE_RATE}
                gapPolicy={data.gapPolicy}
                label={t.GAP_POLICY_LABEL}
                maskLabel={t.GAP_POLICY_MASK}
                onGapPolicyChange={data.handleGapPolicyChange}
            />

            {rateBounds && (
                <RateLegend
                    bounds={rateBounds}
                    title={t.LEGEND_TITLE}
                    zoneMaskLabel={t.LEGEND_ZONE_MASK}
                />
            )}

            <RateColumnPicker
                cancelText={t.WARNING_CANCEL}
                columns={data.columns}
                confirmText={t.RATE_COLUMN_CONFIRM}
                hint={t.RATE_COLUMN_HINT}
                isOpen={data.isColumnPickerOpen}
                onCancel={data.handleCancelUpload}
                onColumnSelected={data.handleColumnSelected}
                title={t.RATE_COLUMN_LABEL}
            />

            <WarningDialog
                cancelText={t.WARNING_CANCEL}
                isOpen={data.isWarningOpen}
                message={data.warningMessage}
                onDismiss={data.handleWarningDismiss}
                title={t.WARNING_TITLE}
            />
        </VerticalContainer>
    );
};

export default PrescriptionSection;
